import React from "react";
import { Plus, X } from "lucide-react";

import { getOpenFundTradeNav } from "../../api/client";
import { quantityUnit } from "../../utils/assetType";
import { HoldingLedgerHistory } from "./HoldingLedgerHistory";
import {
  addNewRow,
  buildLedgerPayload,
  derivedOpeningCosts,
  editHistoryRow,
  groupIssuesByRow,
  hasChanges,
  isBlankOpening,
  isDeleteAll,
  openingCostGap,
  patchNewRow,
  removeNewRow,
  seedLedgerDraft,
  setNewOpening,
} from "./holdingLedgerDraftModel";
import styles from "./holdingEditDialog.module.css";

// 「记录交易」弹窗：两个 tab + 全量可编辑历史，一次提交整本账
// （spec: docs/sdd/astock-agent-transaction-dialog-ledger-edit/spec.md）。
// 候选账本的规则全在 holdingLedgerDraftModel.js，这里只管界面状态。

const TABS = [["transactions", "录入交易记录"], ["holding", "录入持仓"]];

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

// 失焦时把 1350 补成 1350.00、1350.4 补成 1350.40，避免同一列里位数参差。
function normalise(value, digits) {
  const numeric = Number(value);
  return String(value ?? "").trim() && Number.isFinite(numeric) ? numeric.toFixed(digits) : value;
}

// 后端 409/422 的 code 在这里翻成人话；details.issues 一次报全，逐条列出来。
function describeError(error) {
  const payload = error?.payload?.error ?? error?.payload ?? {};
  const issues = (payload.details?.issues ?? [])
    .map((issue) => issue?.message || issue?.issue_id)
    .filter(Boolean);
  // 逐条原因里已经写明是哪一笔、错在哪，**不再加概述句**（2026-09-04 产品，前后提了三次）。
  if (issues.length) return issues.join("\n");
  return {
    binding_asset_mismatch: "表单里的标的和当前页面的标的不是同一只，没有写入。请关掉弹窗重新打开。",
    holding_candidate_not_ready: "持仓信息还不完整，没有录入。",
    tracking_price_unavailable: "缺少可用于开始跟踪的行情数据，这笔持仓暂时录不进来。",
    effective_date_in_future: "建仓日期不能晚于今天。",
    trade_date_in_future: "交易日期不能晚于今天。",
    effective_date_invalid: "建仓日期格式不对，请重新选择。",
  }[payload.code] || payload.message || error?.message || "提交失败，请稍后再试。";
}

function Field({ label, suffix, children }) {
  return <label className={styles.field}><span>{label}</span><div>{children}{suffix && <small>{suffix}</small>}</div></label>;
}

// 盖在「记录交易」上面的确认弹窗：删光、放弃修改共用（2026-09-15 产品：不再嵌在弹窗底部）。
// 下层由调用方设 inert，焦点和点击都进不去；默认焦点在安全的那个按钮，Esc / 点遮罩 = 取消。
function ConfirmDialog({ id, title, body, cancelLabel, confirmLabel, busy = false, onCancel, onConfirm }) {
  const cancelRef = React.useRef(null);
  React.useEffect(() => { cancelRef.current?.focus(); }, []);
  return (
    <div className={styles.confirmBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <section className={styles.confirmDialog} role="alertdialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={body ? `${id}-body` : undefined}>
        <h3 id={`${id}-title`}>{title}</h3>
        {body && <p id={`${id}-body`}>{body}</p>}
        <footer className={styles.dialogActions}>
          <button type="button" ref={cancelRef} onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button type="button" onClick={onConfirm} disabled={busy}>{busy ? "正在提交…" : confirmLabel}</button>
        </footer>
      </section>
    </div>
  );
}

// 「删除平安银行的全部记录？」正文：说清删掉几条、会不会从持仓列表移出
function deleteAllCopy(draft, target, assetCode) {
  const label = target?.assetName || assetCode || "该标的";
  const trades = draft?.history.length ?? 0;
  const openings = draft?.openings.length ?? 0;
  const parts = [trades && `${trades} 笔交易`, openings && `${openings} 条录入持仓`].filter(Boolean);
  const what = parts.length > 1 ? `${trades + openings} 条记录（${parts.join("、")}）` : parts[0] || "全部记录";
  // 已清仓移出的持仓本来就不在持仓列表里，不提移出（decisions.md 2026-09-14）
  return {
    title: `删除${label}的全部记录？`,
    body: target?.cleared ? `${what}会被删除。` : `${what}会被删除，${label}将从持仓列表移出。`,
    confirmLabel: target?.cleared ? "删除" : "删除并移出",
  };
}

function isOpenFundAsset(assetType, assetCode) {
  const normalizedType = String(assetType || "").trim().toLowerCase();
  if (normalizedType) return normalizedType === "open_fund";
  return String(assetCode || "").trim().toUpperCase().endsWith(".OF");
}

const text = (value) => String(value ?? "").trim();
const blankNewRow = (row) => !text(row.quantity) && !text(row.price);
const emptyNewRow = () => ({ trade_date: today(), action: "BUY", price: "", quantity: "", navStatus: "idle" });

// 一笔交易的身份：日期+方向+数量+价格。后端的 confirmation_key 就是按这几样算的，
// 所以价格一改，凭据自然失配——这正是我们要的。
function fingerprintOf(trade) {
  return [trade.trade_date, text(trade.action).toUpperCase(), text(trade.quantity), text(trade.price)].join("|");
}

function confirmationKeyMap(issues) {
  const map = new Map();
  (issues || []).forEach((issue) => {
    const confirmation = issue?.price_confirmation;
    if (confirmation?.confirmation_key && confirmation.trade_fingerprint) {
      map.set(fingerprintOf(confirmation.trade_fingerprint), confirmation.confirmation_key);
    }
  });
  return map;
}

function seed(target, proposal) {
  const draft = seedLedgerDraft({
    openingPosition: target.openingPosition,
    transactions: target.transactions,
    proposal,
  });
  const newRows = draft.newRows.map((row) => ({ navStatus: "idle", action: "BUY", ...row }));
  return newRows.length ? { ...draft, newRows } : addNewRow(draft, emptyNewRow());
}

export function HoldingEditDialog({
  open,
  assetCode: pageAssetCode,
  assetType = null,
  proposal = null,
  target = null,
  busy,
  onCancel,
  onSubmit,
}) {
  const [draft, setDraft] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = React.useState(false);
  // 有没提交的修改时，任何关闭路径（遮罩、Esc、×、取消修改）都先问一句（2026-09-14 产品）
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  // 上一次提交被挡住的问题：定位到行，并带着异常价确认凭据。表单一动就作废。
  const [blocked, setBlocked] = React.useState({ issues: [], byKey: new Map() });
  // 场外基金历史行按日期取净值的状态（行 key → { status, error }）；在途请求的日期记在 ref 里防串
  const [historyNav, setHistoryNav] = React.useState({});
  const historyNavKeys = React.useRef({});
  const dialogRef = React.useRef(null);
  // 以写入目标的标的为准：打开弹窗会把页面绑定换到这只标的的持仓，
  // 行情视图随之清空，页面传进来的标的在行情态下会变成空串（T11-⑦ 实测「缺少标的。」）。
  const assetCode = (target?.status === "ready" && target.assetCode) || pageAssetCode;
  const isOpenFund = isOpenFundAsset(assetType, assetCode);
  // 可见文字按「份 / 股」走；aria-label 仍叫「股数」——它是 E2E 与走查脚本的定位符，
  // 改名要连带改十几处用例。ponytail: 读屏会把基金的份额读成「股数」，要修时一起改用例。
  const unit = quantityUnit(assetType, assetCode);

  // 写入目标读到（或提交成功后 revision 前进）才重新铺表单；409 不会改 revision，草稿原样保留。
  React.useEffect(() => {
    if (!open || target?.status !== "ready") return;
    setDraft(seed(target, proposal));
    historyNavKeys.current = {};
    setHistoryNav({});
    setBlocked({ issues: [], byKey: new Map() });
    setConfirmDeleteAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.status, target?.revision, target?.binding?.binding_id]);

  React.useEffect(() => {
    if (open) return;
    setDraft(null);
    setSubmitting(false);
    setError("");
    setResult(null);
    setConfirmDeleteAll(false);
    setConfirmDiscard(false);
    setBlocked({ issues: [], byKey: new Map() });
    historyNavKeys.current = {};
    setHistoryNav({});
  }, [open]);

  const navSignature = isOpenFund && draft
    ? draft.newRows.map((row) => `${row.key}:${row.trade_date}`).join("|")
    : "";
  React.useEffect(() => {
    if (!open || !isOpenFund || !assetCode || !draft) return;
    const code = String(assetCode).toUpperCase();
    draft.newRows
      .filter((row) => row.trade_date && row.navLookupKey !== `${code}|${row.trade_date}`)
      .forEach((pending) => {
        const requestKey = `${code}|${pending.trade_date}`;
        const patchByKey = (patch) => setDraft((current) => current && ({
          ...current,
          newRows: current.newRows.map((row) => (
            row.key === pending.key && row.navLookupKey === requestKey ? { ...row, ...patch } : row
          )),
        }));
        setDraft((current) => current && ({
          ...current,
          newRows: current.newRows.map((row) => (row.key === pending.key
            ? { ...row, price: "", navStatus: "loading", navError: "", navLookupKey: requestKey }
            : row)),
        }));
        void getOpenFundTradeNav(assetCode, pending.trade_date)
          .then((payload) => {
            const resolved = payload?.data ?? {};
            const missing = resolved.price === null || resolved.price === undefined;
            const resolvedDate = String(resolved.trade_date || pending.trade_date);
            patchByKey({
              trade_date: resolvedDate,
              price: missing ? "" : String(resolved.price),
              navStatus: missing ? "error" : "ready",
              navError: missing ? "没有取得该日期对应的单位净值。" : "",
              navLookupKey: `${code}|${resolvedDate}`,
            });
          })
          .catch((lookupError) => patchByKey({ price: "", navStatus: "error", navError: describeError(lookupError) }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCode, isOpenFund, navSignature, open]);

  // 场外基金历史行改了日期：和新增行一样按新日期取净值，填进只读价格框。
  // 原来价格停在旧净值又改不了，提交被「必须使用某日净值」挡死（2026-09-15 走查 G-01）。
  const historyDateSignature = isOpenFund && draft
    ? draft.history.map((row) => `${row.key}:${row.edits.trade_date ?? ""}`).join("|")
    : "";
  React.useEffect(() => {
    if (!open || !isOpenFund || !assetCode || !draft) return;
    const code = String(assetCode).toUpperCase();
    draft.history.forEach((row) => {
      const date = row.edits.trade_date ?? row.stored.trade_date;
      const requestKey = `${code}|${date}`;
      if (!date || (historyNavKeys.current[row.key] ?? `${code}|${row.stored.trade_date}`) === requestKey) return;
      historyNavKeys.current[row.key] = requestKey;
      const settle = (patch, nav) => {
        if (historyNavKeys.current[row.key] !== requestKey) return;
        setDraft((current) => current && editHistoryRow(current, row.key, patch));
        setHistoryNav((current) => ({ ...current, [row.key]: nav }));
      };
      settle({ price: "" }, { status: "loading", error: "" });
      void getOpenFundTradeNav(assetCode, date)
        .then((payload) => {
          const resolved = payload?.data ?? {};
          const missing = resolved.price === null || resolved.price === undefined;
          const resolvedDate = String(resolved.trade_date || date);
          settle(
            { trade_date: resolvedDate, price: missing ? "" : String(resolved.price) },
            { status: missing ? "error" : "ready", error: missing ? "没有取得该日期对应的单位净值。" : "" },
          );
          historyNavKeys.current[row.key] = `${code}|${resolvedDate}`;
        })
        .catch((lookupError) => settle({ price: "" }, { status: "error", error: describeError(lookupError) }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCode, isOpenFund, historyDateSignature, open]);

  const unsaved = Boolean(draft) && result === null && hasChanges(draft);
  const requestClose = React.useCallback(() => {
    if (busy || submitting) return;
    if (unsaved) setConfirmDiscard(true);
    else onCancel();
  }, [busy, onCancel, submitting, unsaved]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      // 确认框开着时 Esc 只收起确认框、回到编辑
      if (confirmDiscard) setConfirmDiscard(false);
      else if (confirmDeleteAll) setConfirmDeleteAll(false);
      else requestClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [confirmDeleteAll, confirmDiscard, open, requestClose]);

  // 焦点困在弹窗里：打开时移进来，Tab 走到头回到开头。原来焦点留在页面上的
  // 「记录交易」按钮，连按 Tab 走的是弹窗背后的图表控件（2026-09-16 走查 S-01）。
  const focusables = () => [
    ...(dialogRef.current?.querySelectorAll(
      'input:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ) ?? []),
  ].filter((node) => node.offsetParent !== null);
  React.useEffect(() => {
    if (!open || !draft || confirmDeleteAll || confirmDiscard) return undefined;
    if (!dialogRef.current?.contains(document.activeElement)) focusables()[0]?.focus();
    const onKeyDown = (event) => {
      if (event.key !== "Tab" || !dialogRef.current) return;
      const nodes = focusables();
      if (!nodes.length) return;
      const [first] = nodes;
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (!dialogRef.current.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, Boolean(draft), confirmDeleteAll, confirmDiscard]);

  // 被挡住后把焦点移到第一个出问题的行，读屏和键盘用户不必自己找。
  React.useEffect(() => {
    const [firstKey] = blocked.byKey.keys();
    if (!firstKey) return;
    const row = dialogRef.current?.querySelector(`[data-row-key="${firstKey}"]`);
    (row?.querySelector('[aria-invalid="true"]') ?? row?.querySelector("input, button"))?.focus();
  }, [blocked]);

  // 记录没读到之前不出弹窗，不给「正在读取」的过渡（用户 2026-09-15）；读失败仍打开弹窗说原因
  if (!open || (target?.status !== "error" && !draft)) return null;

  const loading = target?.status !== "ready" || !draft;
  const locked = busy || submitting || loading || result !== null;
  const confirmationKeys = confirmationKeyMap(blocked.issues);

  // 上一次的"确认"只对**当时提交的那一批**有效；表单一动就作废，否则会替用户确认他没确认过的价
  // （2026-09-04 用户提出）。
  const updateDraft = (updater) => {
    setBlocked({ issues: [], byKey: new Map() });
    setConfirmDeleteAll(false);
    // 不收起「放弃修改？」：弹出它时焦点移走，输入框失焦补小数位也走这里，一收就等于没弹
    setDraft((current) => (typeof updater === "function" ? updater(current) : updater));
  };

  const payload = draft ? buildLedgerPayload(draft, { assetCode, unit }) : null;
  const withKeys = payload && {
    ...payload,
    transactions: payload.transactions.map((row) => {
      const key = confirmationKeys.get(fingerprintOf(row));
      return key ? { ...row, price_confirmation_key: key } : row;
    }),
  };
  const confirmingPrices = Boolean(withKeys?.transactions.some((row) => confirmationKeys.has(fingerprintOf(row))));
  // 「录入持仓」tab 的表单是新增的一批（2026-09-16），已提交的批次在历史区改
  const opening = draft?.newOpening;
  const derivedCost = derivedOpeningCosts(opening);
  const costGap = openingCostGap(opening);
  const openingReady = (values) => Boolean(text(values?.quantity) && (text(values?.cost_price) || text(values?.total_cost)));
  const ready = Boolean(draft)
    && hasChanges(draft)
    && draft.newRows.filter((row) => !blankNewRow(row)).every((row) => (
      row.trade_date && text(row.quantity) && text(row.price) && (!isOpenFund || row.navStatus === "ready")
    ))
    && draft.history.filter((row) => !row.deleted).every((row) => {
      const values = { ...row.stored, ...row.edits };
      return values.trade_date && text(values.quantity) && text(values.price);
    })
    && (isBlankOpening(opening) || openingReady(opening))
    && draft.openings.filter((batch) => !batch.deleted).every((batch) => openingReady(batch.values));

  async function submit({ confirmedDeleteAll = false } = {}) {
    if (!ready || locked) return;
    if (isDeleteAll(draft) && !confirmedDeleteAll) {
      setConfirmDeleteAll(true);
      return;
    }
    const submittedDraft = draft;
    setConfirmDeleteAll(false);
    setError("");
    setSubmitting(true);
    try {
      const outcome = await onSubmit(withKeys);
      const warnings = outcome?.warnings ?? [];
      const failed = outcome?.calculationStatus === "failed";
      // 没有提醒、核算也跑通了就直接关——右侧已经变了，那就是回执。
      if (!warnings.length && !failed) {
        onCancel();
        return;
      }
      setResult({ warnings, failed, failureReason: outcome?.calculationFailureReason ?? "" });
    } catch (submitError) {
      // 一笔都没写进去，草稿原样留着。问题按提交时的行号定位到行。
      const issues = submitError?.payload?.error?.details?.issues ?? [];
      const { byKey, unmapped } = groupIssuesByRow(submittedDraft, issues);
      setBlocked({ issues, byKey });
      // 已经标到行上的原因只在那一行下面说一次；上方只留归不到行的（走查 W-01）
      setError(issues.length ? unmapped.join("\n") : describeError(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  const patchRow = (index, patch) => updateDraft((current) => patchNewRow(current, index, patch));
  // 失焦补小数位：值没变就什么都不做。否则会被当成一次修改、清掉上次被挡的问题和确认凭据——
  // 焦点停在出问题的价格框时点「确认价格并提交」，失焦先把按钮变回「提交」，这次点击就落空了（2026-09-15 走查）。
  const blurNormalise = (value, digits, apply) => {
    const next = normalise(value ?? "", digits);
    if (next !== (value ?? "")) apply(next);
  };

  return (
    <div
      className={styles.dialogBackdrop}
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
    >
      <section ref={dialogRef} className={styles.holdingDialog} role="dialog" aria-modal="true" aria-labelledby="holding-edit-title" inert={confirmDeleteAll || confirmDiscard}>
        <header className={styles.dialogHeader}>
          <h2 id="holding-edit-title">记录交易</h2>
          <button type="button" onClick={requestClose} disabled={busy || submitting} aria-label="关闭弹窗"><X size={19} /></button>
        </header>
        <nav className={styles.tabs} role="tablist" aria-label="交易记录方式">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={(draft?.tab ?? "transactions") === key}
              disabled={loading}
              onClick={() => setDraft((current) => current && { ...current, tab: key })}
            >{label}</button>
          ))}
        </nav>
        <div className={styles.dialogBody}>
          {target?.status === "error" && <p className={styles.submitError} role="alert">{describeError(target.error)}</p>}
          {draft && (draft.tab === "holding" ? (
            <section className={styles.entry} role="tabpanel" aria-label="录入持仓">
              <strong>录入持仓</strong>
                <div className={styles.entryGrid}>
                  {/* 生效日由用户定（这类持仓基本都是事后补录的）；跟踪日仍由服务端从
                      生效日回退到最近一个有行情的交易日，前端给不了也改不动。 */}
                  <Field label="日期"><input type="date" aria-label="建仓日期" max={today()} value={opening?.effective_date || ""} disabled={locked} onChange={(event) => updateDraft((current) => setNewOpening(current, { effective_date: event.target.value }))} /></Field>
                  <Field label="持仓数量" suffix={unit}><input inputMode="numeric" aria-label="持仓数量" value={opening?.quantity ?? ""} disabled={locked} onChange={(event) => updateDraft((current) => setNewOpening(current, { quantity: event.target.value }))} onBlur={() => { blurNormalise(opening.quantity, 0, (quantity) => updateDraft((current) => setNewOpening(current, { quantity }))); }} /></Field>
                  <Field label="持仓成本" suffix={`元 / ${unit}`}><input inputMode="decimal" aria-label="持仓成本" placeholder={derivedCost.cost_price ?? undefined} value={opening?.cost_price ?? ""} disabled={locked} onChange={(event) => updateDraft((current) => setNewOpening(current, { cost_price: event.target.value }))} onBlur={() => { blurNormalise(opening.cost_price, 2, (cost_price) => updateDraft((current) => setNewOpening(current, { cost_price }))); }} /></Field>
                  <Field label="总成本" suffix="元"><input inputMode="decimal" aria-label="总成本" placeholder={derivedCost.total_cost ?? undefined} value={opening?.total_cost ?? ""} disabled={locked} onChange={(event) => updateDraft((current) => setNewOpening(current, { total_cost: event.target.value }))} onBlur={() => { blurNormalise(opening.total_cost, 2, (total_cost) => updateDraft((current) => setNewOpening(current, { total_cost }))); }} /></Field>
                </div>
              {costGap && (
                <p className={styles.costHint} role="status">
                  总成本和「持仓数量 × 持仓成本」（{Number(costGap.expected).toLocaleString("zh-CN", { minimumFractionDigits: 2 })} 元）相差较大，请确认是否填错。
                </p>
              )}
            </section>
          ) : (
            <section className={styles.entry} role="tabpanel" aria-label="录入交易记录">
              <strong>录入交易记录</strong>
              {draft.newRows.map((row, index) => {
                const issue = blocked.byKey.get(row.key);
                const flagged = (field) => (issue?.fields.has(field) ? "true" : undefined);
                return (
                  <div className={styles.entryGrid} key={row.key} data-row-key={row.key}>
                    <Field label={index === 0 ? "日期" : ""}><input type="date" aria-label={`第 ${index + 1} 笔日期`} className={flagged("trade_date") && styles.flaggedInput} aria-invalid={flagged("trade_date")} value={row.trade_date} disabled={locked} onChange={(event) => patchRow(index, isOpenFund ? { trade_date: event.target.value, price: "", navStatus: "idle", navError: "", navLookupKey: null } : { trade_date: event.target.value })} /></Field>
                    <Field label={index === 0 ? "买卖方向" : ""}><select aria-label={`第 ${index + 1} 笔买卖方向`} value={text(row.action).toUpperCase() === "SELL" ? "卖出" : "买入"} disabled={locked} onChange={(event) => patchRow(index, { action: event.target.value === "卖出" ? "SELL" : "BUY" })}><option>买入</option><option>卖出</option></select></Field>
                    <Field label={index === 0 ? (unit === "份" ? "份额" : "股数") : ""} suffix={unit}><input inputMode="numeric" aria-label={`第 ${index + 1} 笔股数`} className={flagged("quantity") && styles.flaggedInput} aria-invalid={flagged("quantity")} value={row.quantity} disabled={locked} onChange={(event) => patchRow(index, { quantity: event.target.value })} onBlur={() => blurNormalise(row.quantity, 0, (quantity) => patchRow(index, { quantity }))} /></Field>
                    <Field label={index === 0 ? (isOpenFund ? "确认净值" : "价格") : ""} suffix="元"><input inputMode="decimal" aria-label={`第 ${index + 1} 笔价格`} className={flagged("price") && styles.flaggedInput} aria-invalid={flagged("price") || (row.navStatus === "error" && !blankNewRow(row) ? "true" : undefined)} value={row.price} placeholder={isOpenFund && row.navStatus === "loading" ? "正在获取净值…" : undefined} readOnly={isOpenFund} disabled={locked} onChange={(event) => patchRow(index, { price: event.target.value })} onBlur={() => { if (!isOpenFund) blurNormalise(row.price, 2, (price) => patchRow(index, { price })); }} /></Field>
                    <button
                      type="button"
                      className={styles.removeRow}
                      disabled={locked || draft.newRows.length === 1}
                      aria-label={`删除第 ${index + 1} 笔`}
                      onClick={() => updateDraft((current) => removeNewRow(current, index))}
                    ><X size={15} /></button>
                    {/* 空行不报净值错误：新增行默认今天，当天多半还没有净值，用户什么都没填就看到红字（走查 G-01） */}
                    {row.navError && !blankNewRow(row) && <p className={styles.rowIssue} role="alert">第 {index + 1} 笔：{row.navError}</p>}
                    {issue && <p className={styles.rowIssue} role="alert">{issue.text}</p>}
                  </div>
                );
              })}
              <button
                type="button"
                className={styles.addRow}
                disabled={locked}
                onClick={() => updateDraft((current) => addNewRow(current, emptyNewRow()))}
              ><Plus size={16} />添加一行</button>
            </section>
          ))}
          {error && <p className={styles.submitError} role="alert">{error}</p>}
          {result && (
            <div className={result.failed ? styles.submitError : styles.submitResult} role="status">
              <strong>{result.failed ? "记录已保存，核算失败" : `已记录，有 ${result.warnings.length} 条提醒`}</strong>
              {result.failed && <span>{result.failureReason || "核算暂时失败，请稍后重试。"}</span>}
              {result.warnings.map((warning, index) => (
                <span key={warning.issue_id || index}>{warning.message || warning.issue_id}</span>
              ))}
            </div>
          )}
          {draft && (
            <HoldingLedgerHistory
              draft={draft}
              onChange={updateDraft}
              disabled={locked}
              isOpenFund={isOpenFund}
              unit={unit}
              issues={blocked.byKey}
              navState={historyNav}
            />
          )}
          <footer className={styles.dialogActions}>
            {result ? (
              <button type="button" onClick={onCancel}>关闭</button>
            ) : (
              <>
                <button type="button" onClick={requestClose} disabled={busy || submitting}>取消修改</button>
                {/* 上一次被异常价挡住、用户没改价就再点一次 = "就是这个价"。按钮得把这件事
                    说出来（2026-09-04 用户提出）。 */}
                <button type="button" onClick={() => { void submit(); }} disabled={!ready || locked}>{
                  submitting ? "正在提交…" : (confirmingPrices ? "确认价格并提交" : "提交")
                }</button>
              </>
            )}
          </footer>
        </div>
      </section>
      {confirmDeleteAll && (
        <ConfirmDialog
          id="delete-all"
          {...deleteAllCopy(draft, target, assetCode)}
          cancelLabel="取消"
          busy={submitting}
          onCancel={() => setConfirmDeleteAll(false)}
          onConfirm={() => { void submit({ confirmedDeleteAll: true }); }}
        />
      )}
      {confirmDiscard && (
        <ConfirmDialog
          id="discard"
          title="放弃修改？"
          cancelLabel="继续编辑"
          confirmLabel="放弃修改"
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={onCancel}
        />
      )}
    </div>
  );
}
